'use client';

import React, { useEffect, useState } from 'react';

import { css, cva, cx } from '@pt-group-fe/styled-system/css';
import { Stack, Wrap } from '@pt-group-fe/styled-system/jsx';
import { mobileNavigation } from '@pt-group-fe/styled-system/recipes';
import * as DialogRx from '@radix-ui/react-dialog';

import { useHeaderBanner } from '@lib/hooks';
import { Cms_FlyoutMenu, Cms_Link, Cms_SitemapTreeItem } from '@lib/services/cms';
import { useTranslation } from '@lib/services/i18n/client';
import { Button } from '@lib/ui/button';
import { CmsLink } from '@lib/ui/cmsLink';
import { Icon } from '@lib/ui/icon';
import { MenuButton } from '@lib/ui/menuButton';

import { LanguageNavigation, LanguageNavigationProps } from '../languageNavigation';

import { useMobileNavStore } from './mobileNavigationStore';
import { MobileSubItems } from './MobileSubItems';
import { NavItemMobile } from './NavItemMobile';

const contentSlider = cva({
  base: {
    transition: 'transform 0.3s ease-out',
    transform: 'translateX(100%)',
    opacity: 0,
    h: 0,
    visibility: 'hidden'
  },
  variants: {
    hasCurrentSubMenu: {
      true: {
        transform: 'translateX(0%)',
        opacity: 1,
        h: 'auto',
        visibility: 'visible'
      }
    },
    isBackClicked: {
      true: {
        animation: 'exitToRight 400ms cubic-bezier(0.16, 1, 0.3, 1)',
        animationDelay: '0.2s',
        transform: 'translateX(100%)'
      }
    }
  }
});

type MobileNavigationProps = {
  flyoutMenus?: Cms_FlyoutMenu[];
  items?: Cms_SitemapTreeItem[];
  activeItemId?: number | undefined;
  hasHeaderBanner?: boolean;
  langNavProps?: LanguageNavigationProps;
  footerLinks?: Cms_Link[];
};

const MobileNavigation: React.FC<MobileNavigationProps> = ({
  items,
  flyoutMenus,
  activeItemId,
  hasHeaderBanner,
  langNavProps,
  footerLinks
}) => {
  const [isBackClicked, setIsBackClicked] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const setCurrentSubMenuContent = useMobileNavStore(state => state.setCurrentSubMenuContent);
  const currentSubMenuContent = useMobileNavStore(state => state.item);
  const hasCurrentSubMenu = currentSubMenuContent !== null;
  const isBannerActive = useHeaderBanner(hasHeaderBanner);
  const { t } = useTranslation();
  const { overlay, content, contentInner, backWrap, backLabel, backIcon } = mobileNavigation({
    isBannerActive,
    hasCurrentSubMenu,
    isBackClicked
  });

  const handleSubmenuAppereance = (item: Cms_SitemapTreeItem) => {
    setCurrentSubMenuContent(item);
    setIsBackClicked(false);
  };

  const handleBackClick = () => {
    setIsBackClicked(true);
    setTimeout(() => {
      setCurrentSubMenuContent(null);
    }, 300);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsOpen(false);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  return (
    <DialogRx.Root onOpenChange={setIsOpen} open={isOpen}>
      <div>
        <DialogRx.Trigger asChild>
          <MenuButton />
        </DialogRx.Trigger>
      </div>

      <DialogRx.Portal>
        <DialogRx.Overlay className={overlay} />
        <DialogRx.Content
          className={cx(content, css({ display: 'flex', flexDir: 'column', zIndex: 2000 }))}
        >
          <div className={contentInner}>
            {items?.map(item => (
              <NavItemMobile
                data={item}
                key={item.id}
                handleSubmenuAppereance={handleSubmenuAppereance}
                activeItemId={activeItemId}
              />
            ))}
          </div>

          <div
            className={contentSlider({
              isBackClicked,
              hasCurrentSubMenu
            })}
          >
            <div className={backWrap} onClick={() => handleBackClick()}>
              <Icon i="chevron-left" size="small" className={backIcon} />
              <span className={backLabel}>{t('uiWeb.mobileNav.back')}</span>
            </div>
            <MobileSubItems
              data={currentSubMenuContent}
              depth={0}
              activeItemId={activeItemId}
              flyoutMenus={flyoutMenus}
              itemId={currentSubMenuContent?.id}
            />
          </div>

          {(langNavProps || (footerLinks && footerLinks?.length > 0)) && (
            <Stack className={css({ mt: 'auto' })} gap={8}>
              {footerLinks && footerLinks.length > 0 && (
                <Wrap gap={4}>
                  {footerLinks.map(l => (
                    <CmsLink
                      key={l.id}
                      link={l}
                      className={css({ display: 'inline-block', flexGrow: 1 })}
                    >
                      <Button
                        size="small"
                        icon="arrow-right"
                        className={css({ w: 'full' })}
                        variant={l.buttonType}
                      >
                        {l.title}
                      </Button>
                    </CmsLink>
                  ))}
                </Wrap>
              )}
              {langNavProps && <LanguageNavigation {...langNavProps} position="mobile" />}
            </Stack>
          )}
        </DialogRx.Content>
      </DialogRx.Portal>
    </DialogRx.Root>
  );
};

export { MobileNavigation };
export type { MobileNavigationProps };
